import { atom } from "jotai";
import {
  AccountingDataFilterInfo,
  AccountingLinkedDeduction,
  AccountingRemittanceFilterInfo,
  accountingAtom,
  currentPageAtom,
  filterInfoAtom,
  jeCurrentPageAtom,
  jeFilterInfoAtom,
  jePageLimitAtom,
  pageLimitAtom,
  remittanceFilterInfoAtom,
} from "./accounting.atoms";
import { AppConfig } from "@/infrastructure/services/config/WebApp.config";
import { GraphQlAdapter } from "@/infrastructure/adapters/graphQl/GraphQl.adapter";
import { DeductionRepository } from "@/infrastructure/repositories/deduction";
import { GetAccountingDataUseCase } from "@/core/usecases/deduction";
import { BrandMember } from "@/core/entities/lookup/brandMember";
import { Deduction } from "@/core/entities/deduction";
import {
  CheckCreditMemoDocumentNumberUseCase,
  CreateCreditMemoBatchUseCase,
  CreateJournalEntryUseCase,
  CreateRemittanceCreditMemoUseCase,
  GetAccountingJournalEntriesUseCase,
  GetAccountingRemittancesUseCase,
  GetAccountingSystemInvoicesGroupedUseCase,
  GetAccountingSystemInvoicesUseCase,
} from "@/core/usecases/accounting";
import { AccountingSystemInvoiceRepository } from "@/infrastructure/repositories/accounting/AccountingInvoice.repository";
import { CreditMemoBatchRepository } from "@/infrastructure/repositories/accounting/CreditMemoBatch.repository";
import { RemittanceCreditMemoRepository } from "@/infrastructure/repositories/accounting/RemittanceCreditMemo.repository";
import { AccountingRemittanceRepository } from "@/infrastructure/repositories/accounting/AccountingRemittance.repository";
import { AccountingJournalEntryRepository } from "@/infrastructure/repositories/accounting/AccountingJournalEntry.repository";
import { JournalEntryRepository } from "@/infrastructure/repositories/accounting/JournalEntry.repository";
import { CreditMemoDocumentNumberRepository } from "@/infrastructure/repositories/accounting/CreditMemoDocumentNumber.repository";
import { AccountingSystemInvoice } from "@/core/entities/accounting";
import { AccountingRemittance } from "@/core/entities/accounting/AccountingRemittance";
import { getEmptyAccountingRemittanceFilterInfo } from "@/store/deduction/deductionFilter/utils";
import { createLogger } from "@/infrastructure/services/logger";
import {
  AccountingSystemInvoicesGroupedData,
  ICreateJournalEntryParams,
  ICreateRemittanceCreditMemoParams,
} from "@/core/repositories/accounting";

const MAX_GROUPED_INVOICES_CACHE_SIZE = 10;

const upsertGroupedInvoicesCache = (
  currentMap: Record<string, AccountingSystemInvoicesGroupedData>,
  cacheKey: string,
  data: AccountingSystemInvoicesGroupedData
): Record<string, AccountingSystemInvoicesGroupedData> => {
  const nextMap = {
    ...currentMap,
    [cacheKey]: data,
  };
  const cacheKeys = Object.keys(nextMap);
  if (cacheKeys.length <= MAX_GROUPED_INVOICES_CACHE_SIZE) {
    return nextMap;
  }

  return cacheKeys
    .filter(key => key !== cacheKey)
    .slice(0, cacheKeys.length - MAX_GROUPED_INVOICES_CACHE_SIZE)
    .reduce<Record<string, AccountingSystemInvoicesGroupedData>>(
      (result, keyToRemove) => {
        delete result[keyToRemove];
        return result;
      },
      { ...nextMap }
    );
};

const removeGroupedInvoicesCacheEntry = (
  currentMap: Record<string, AccountingSystemInvoicesGroupedData>,
  cacheKey: string
): Record<string, AccountingSystemInvoicesGroupedData> => {
  const nextMap = { ...currentMap };
  delete nextMap[cacheKey];
  return nextMap;
};

const logger = createLogger("accounting.actions");

const client = GraphQlAdapter.getInstance().getClient();
const _appConfig = AppConfig.getInstance();
const deductionRepository = new DeductionRepository(client);
const accountingSystemInvoiceRepository = new AccountingSystemInvoiceRepository(client);
const creditMemoBatchRepository = new CreditMemoBatchRepository(client);
const remittanceCreditMemoRepository = new RemittanceCreditMemoRepository(client);
const accountingRemittanceRepository = new AccountingRemittanceRepository(client);
const accountingJournalEntryRepository = new AccountingJournalEntryRepository(client);
const journalEntryRepository = new JournalEntryRepository(client);
const creditMemoDocumentNumberRepository = new CreditMemoDocumentNumberRepository(client);
const TIME_ZONE = _appConfig.TIMEZONE;

export const getAccountingDataAtom = atom(
  null,
  async (
    get,
    set,
    { isLoading = true, isRefreshing = false }: { isLoading?: boolean; isRefreshing?: boolean } = {}
  ) => {
    try {
      set(accountingAtom, {
        ...get(accountingAtom),
        isLoading,
        isRefreshing,
        error: null,
        filterInfo: {
          ...get(accountingAtom).filterInfo,
          lastFilteredAt: new Date(),
        },
      });

      const filterInfo = get(filterInfoAtom);
      const currentPage = get(currentPageAtom);
      const pageLimit = get(pageLimitAtom);
      const getAccountingDataUseCase = new GetAccountingDataUseCase(deductionRepository);
      const response = await getAccountingDataUseCase.execute(
        {
          page_number: currentPage,
          page_size: pageLimit,
          sort_column: filterInfo.sortColumn,
          sort_order: filterInfo.sortOrder,
          time_zone: TIME_ZONE,
          search: filterInfo.search,
          check_from_date: filterInfo.checkFromDate,
          check_to_date: filterInfo.checkToDate,
          invoice_from_date: filterInfo.invoiceFromDate,
          invoice_to_date: filterInfo.invoiceToDate,
          purchaser_ids: filterInfo.purchaser.map(purchaser => Number(purchaser.value)),
          distribution_center_ids: filterInfo.dc.map(distributionCenter => Number(distributionCenter.value)),
          glCode_ids: filterInfo.glCode.map(glCode => Number(glCode.value)),
          analyst_user_ids: filterInfo.analyst.map(analystUser => Number(analystUser.value)),
          approver_user_ids: filterInfo.approver.map(approverUser => Number(approverUser.value)),
        },
        isLoading || isRefreshing
      );

      set(accountingAtom, {
        ...get(accountingAtom),
        accountingDataItems: response?.getAccountingData ?? [],
        columns: response?.columns ?? [],
        totalCount: response?.totalCount ?? 0,
        currentPage: response?.currentPage ?? 1,
        pageLimit: response?.pageLimit ?? 0,
        isLoading: false,
        isRefreshing: false,
        error: null,
      });
    } catch (error) {
      console.error(error);
      set(accountingAtom, {
        ...get(accountingAtom),
        isLoading: false,
        isRefreshing: false,
        error: error as Error,
      });
    }
  }
);

export const setIsLoadingAtom = atom(null, (get, set, isLoading: boolean) => {
  set(accountingAtom, {
    ...get(accountingAtom),
    isLoading,
  });
});

export const setErrorAtom = atom(null, (get, set, error: Error | null) => {
  set(accountingAtom, {
    ...get(accountingAtom),
    error,
  });
});

export const setTotalCountAtom = atom(null, (get, set, totalCount: number) => {
  set(accountingAtom, {
    ...get(accountingAtom),
    totalCount,
  });
});

export const setCurrentPageAtom = atom(null, (get, set, currentPage: number) => {
  set(accountingAtom, {
    ...get(accountingAtom),
    currentPage,
  });
});

export const setFilterAtom = atom(null, (get, set, filter: AccountingDataFilterInfo) => {
  set(accountingAtom, {
    ...get(accountingAtom),
    filterInfo: filter,
    userHasClearedAccountingFilters: isDefaultAccountingFilter(filter),
  });
});

export const clearFilterAtom = atom(null, (get, set, defaultFilter?: AccountingDataFilterInfo) => {
  set(accountingAtom, {
    ...get(accountingAtom),
    isFilterApplied: false,
    userHasClearedAccountingFilters: true,
    filterInfo: defaultFilter ?? {
      lastFilteredAt: null,
      search: null,
      checkFromDate: null,
      checkToDate: null,
      invoiceFromDate: null,
      invoiceToDate: null,
      purchaser: [],
      dc: [],
      glCode: [],
      analyst: [],
      approver: [],
      sortColumn: null,
      sortOrder: null,
      checkDateRange: null,
      invoiceDateRange: null,
    },
  });
});

export const setIsFilterAppliedAtom = atom(null, (get, set, isFilterApplied: boolean) => {
  set(accountingAtom, {
    ...get(accountingAtom),
    isFilterApplied,
  });
});

export const isDefaultAccountingFilter = (filterInfo: AccountingDataFilterInfo | AccountingRemittanceFilterInfo) => {
  return Object.entries(filterInfo)
    .filter(([key]) => key !== "lastFilteredAt" && key !== "sortColumn" && key !== "sortOrder")
    .every(([, value]) => {
      if (Array.isArray(value)) {
        return value.length === 0;
      }
      return value === null || value === "" || value === undefined;
    });
};

export const setLoggedInBrandMemberAtom = atom(null, (get, set, loggedInBrandMember: BrandMember) => {
  set(accountingAtom, {
    ...get(accountingAtom),
    loggedInBrandMember: loggedInBrandMember,
  });
});

export const setSelectedAccountingDataItemsAtom = atom(null, (get, set, selectedAccountingDataItems: Deduction[]) => {
  set(accountingAtom, {
    ...get(accountingAtom),
    selectedAccountingDataItems: selectedAccountingDataItems,
  });
});

export const setSelectedAccountingDataItemAtom = atom(
  null,
  (get, set, selectedAccountingDataItem: Deduction | null) => {
    set(accountingAtom, {
      ...get(accountingAtom),
      selectedAccountingDataItem: selectedAccountingDataItem,
    });
  }
);

export const getAccountingSystemInvoicesAtom = atom(
  null,
  async (
    get,
    set,
    { isLoading = true, isRefreshing = false }: { isLoading?: boolean; isRefreshing?: boolean } = {}
  ) => {
    try {
      set(accountingAtom, {
        ...get(accountingAtom),
        isLoading,
        isRefreshing,
        error: null,
      });

      const getAccountingSystemInvoicesUseCase = new GetAccountingSystemInvoicesUseCase(
        accountingSystemInvoiceRepository
      );
      const response = await getAccountingSystemInvoicesUseCase.execute(isLoading || isRefreshing);

      set(accountingAtom, {
        ...get(accountingAtom),
        accountingSystemInvoices: response?.getAccountingSystemInvoices?.items ?? [],
        accountingSystemKey: response?.getAccountingSystemInvoices?.accountingSystemKey ?? null,
        isLoading: false,
        isRefreshing: false,
      });
    } catch (error) {
      console.error(error);
      set(accountingAtom, {
        ...get(accountingAtom),
        isLoading: false,
        isRefreshing: false,
        error: error as Error,
      });
    }
  }
);

export const getAccountingSystemInvoicesGroupedAtom = atom(
  null,
  async (
    get,
    set,
    {
      checkNumber,
      purchaserKey,
      isRefreshing = false,
    }: {
      checkNumber: string;
      purchaserKey: string;
      isRefreshing?: boolean;
    }
  ) => {
    const cacheKey = `${purchaserKey}:${checkNumber}`;
    const currentState = get(accountingAtom);
    const cachedGroupedInvoices = currentState.groupedInvoicesByCacheKey[cacheKey] ?? null;

    if (!isRefreshing && cachedGroupedInvoices !== null) {
      set(accountingAtom, {
        ...currentState,
        activeGroupedInvoicesCacheKey: cacheKey,
        isLoadingGroupedInvoices: false,
        error: null,
      });
      return;
    }

    try {
      set(accountingAtom, {
        ...currentState,
        groupedInvoicesByCacheKey: isRefreshing
          ? removeGroupedInvoicesCacheEntry(currentState.groupedInvoicesByCacheKey, cacheKey)
          : currentState.groupedInvoicesByCacheKey,
        activeGroupedInvoicesCacheKey: cacheKey,
        isLoadingGroupedInvoices: true,
        error: null,
      });

      const getAccountingSystemInvoicesGroupedUseCase = new GetAccountingSystemInvoicesGroupedUseCase(
        accountingSystemInvoiceRepository
      );
      const response = await getAccountingSystemInvoicesGroupedUseCase.execute(
        { checkNumber, purchaserKey },
        isRefreshing
      );

      const groupedInvoicesData = response?.getAccountingSystemInvoicesGrouped?.data ?? null;

      set(accountingAtom, {
        ...get(accountingAtom),
        groupedInvoicesByCacheKey:
          groupedInvoicesData === null
            ? get(accountingAtom).groupedInvoicesByCacheKey
            : upsertGroupedInvoicesCache(get(accountingAtom).groupedInvoicesByCacheKey, cacheKey, groupedInvoicesData),
        accountingSystemKey: response?.getAccountingSystemInvoicesGrouped?.accountingSystemKey ?? null,
        activeGroupedInvoicesCacheKey: cacheKey,
        isLoadingGroupedInvoices: false,
      });
    } catch (error) {
      logger.error("Failed to fetch grouped accounting system invoices", error);
      set(accountingAtom, {
        ...get(accountingAtom),
        isLoadingGroupedInvoices: false,
        error: error as Error,
      });
    }
  }
);

export const setSelectedAccountingSystemInvoicesAtom = atom(
  null,
  (get, set, selectedAccountingSystemInvoices: AccountingSystemInvoice[]) => {
    set(accountingAtom, {
      ...get(accountingAtom),
      selectedAccountingSystemInvoices: selectedAccountingSystemInvoices,
    });
  }
);

export const setAccountingLinkedDeductionsAtom = atom(
  null,
  (get, set, accountingLinkedDeductions: AccountingLinkedDeduction[]) => {
    set(accountingAtom, {
      ...get(accountingAtom),
      accountingLinkedDeductions: accountingLinkedDeductions,
    });
  }
);

export const createCreditMemoBatchAtom = atom(
  null,
  async (get, set, accountingLinkedDeductions: AccountingLinkedDeduction[]) => {
    try {
      set(accountingAtom, {
        ...get(accountingAtom),
        error: null,
      });

      const response = await new CreateCreditMemoBatchUseCase(creditMemoBatchRepository).execute({
        accountingLinkedDeductions,
      });

      return response.createCreditMemoBatch;
    } catch (error) {
      set(accountingAtom, {
        ...get(accountingAtom),
        isLoading: false,
        error: error as Error,
      });
      throw error;
    }
  }
);

export const createRemittanceCreditMemoAtom = atom(
  null,
  async (get, set, params: ICreateRemittanceCreditMemoParams) => {
    set(accountingAtom, {
      ...get(accountingAtom),
      error: null,
    });

    const response = await new CreateRemittanceCreditMemoUseCase(remittanceCreditMemoRepository).execute(params);

    return response.createRemittanceCreditMemo;
  }
);

export const checkCreditMemoDocumentNumberAtom = atom(null, async (_get, _set, documentNumber: string) => {
  try {
    return await new CheckCreditMemoDocumentNumberUseCase(creditMemoDocumentNumberRepository).execute(documentNumber);
  } catch (error) {
    logger.error("Failed to check credit memo document number", error);
    throw error;
  }
});

export const createJournalEntryAtom = atom(
  null,
  async (get, set, params: ICreateJournalEntryParams) => {
    set(accountingAtom, {
      ...get(accountingAtom),
      error: null,
      jeResult: null,
      isJeResultModalOpen: false,
    });

    try {
      const response = await new CreateJournalEntryUseCase(journalEntryRepository).execute(params);
      const result = response.createJournalEntry;
      const isSuccess = (result?.status ?? "").toUpperCase() === "SUCCESS";

      set(accountingAtom, {
        ...get(accountingAtom),
        jeResult: isSuccess ? result : null,
        isJeResultModalOpen: isSuccess,
      });

      return result;
    } catch (error) {
      set(accountingAtom, {
        ...get(accountingAtom),
        jeResult: null,
        isJeResultModalOpen: false,
      });
      throw error;
    }
  }
);

export const clearJournalEntryResultAtom = atom(null, (get, set) => {
  set(accountingAtom, {
    ...get(accountingAtom),
    jeResult: null,
    isJeResultModalOpen: false,
  });
});

export const getAccountingRemittancesAtom = atom(
  null,
  async (
    get,
    set,
    { isLoading = true, isRefreshing = false }: { isLoading?: boolean; isRefreshing?: boolean } = {}
  ) => {
    try {
      set(accountingAtom, {
        ...get(accountingAtom),
        isLoading,
        isRefreshing,
        error: null,
        remittanceFilterInfo: {
          ...get(accountingAtom).remittanceFilterInfo,
          lastFilteredAt: new Date(),
        },
      });

      const filterInfo = get(remittanceFilterInfoAtom);
      const currentPage = get(currentPageAtom);
      const pageLimit = get(pageLimitAtom);
      const response = await new GetAccountingRemittancesUseCase(accountingRemittanceRepository).execute(
        {
          page_number: currentPage,
          page_size: pageLimit,
          sort_column: filterInfo.sortColumn,
          sort_order: filterInfo.sortOrder,
          time_zone: TIME_ZONE,
          search: filterInfo.search,
          check_from_date: filterInfo.checkFromDate,
          check_to_date: filterInfo.checkToDate,
          purchaser_ids: filterInfo.purchaser.map(purchaser => Number(purchaser.value)),
        },
        isLoading || isRefreshing
      );

      set(accountingAtom, {
        ...get(accountingAtom),
        accountingRemittances: response?.getAccountingRemittances ?? [],
        remittanceColumns: response?.columns ?? [],
        innerColumns: response?.innerColumns ?? [],
        totalCount: response?.totalCount ?? 0,
        currentPage: response?.currentPage ?? 1,
        pageLimit: response?.pageLimit ?? 0,
        isLoading: false,
        isRefreshing: false,
        error: null,
      });
    } catch (error) {
      console.error(error);
      set(accountingAtom, {
        ...get(accountingAtom),
        isLoading: false,
        isRefreshing: false,
        error: error as Error,
      });
    }
  }
);

export const setRemittanceFilterAtom = atom(null, (get, set, filter: AccountingRemittanceFilterInfo) => {
  set(accountingAtom, {
    ...get(accountingAtom),
    remittanceFilterInfo: filter,
  });
});

export const clearRemittanceFilterAtom = atom(null, (get, set) => {
  set(accountingAtom, {
    ...get(accountingAtom),
    isRemittanceFilterApplied: false,
    remittanceFilterInfo: getEmptyAccountingRemittanceFilterInfo(),
  });
});

export const setIsRemittanceFilterAppliedAtom = atom(null, (get, set, isRemittanceFilterApplied: boolean) => {
  set(accountingAtom, {
    ...get(accountingAtom),
    isRemittanceFilterApplied,
  });
});

export const setExpandedRemittanceIdAtom = atom(null, (get, set, expandedRemittanceId: number | null) => {
  set(accountingAtom, {
    ...get(accountingAtom),
    expandedRemittanceId,
  });
});

export const setSelectedAccountingRemittancesAtom = atom(
  null,
  (get, set, selectedAccountingRemittances: AccountingRemittance[]) => {
    set(accountingAtom, {
      ...get(accountingAtom),
      selectedAccountingRemittances,
    });
  }
);

export const getAccountingJournalEntriesAtom = atom(
  null,
  async (
    get,
    set,
    { isLoading = true, isRefreshing = false }: { isLoading?: boolean; isRefreshing?: boolean } = {}
  ) => {
    try {
      set(accountingAtom, {
        ...get(accountingAtom),
        jeIsLoading: isLoading,
        jeIsRefreshing: isRefreshing,
        jeError: null,
        jeFilterInfo: {
          ...get(accountingAtom).jeFilterInfo,
          lastFilteredAt: new Date(),
        },
      });

      const filterInfo = get(jeFilterInfoAtom);
      const currentPage = get(jeCurrentPageAtom);
      const pageLimit = get(jePageLimitAtom);
      const response = await new GetAccountingJournalEntriesUseCase(accountingJournalEntryRepository).execute(
        {
          page_number: currentPage,
          page_size: pageLimit,
          sort_column: filterInfo.sortColumn,
          sort_order: filterInfo.sortOrder,
          search: filterInfo.search,
          check_from_date: filterInfo.checkFromDate,
          check_to_date: filterInfo.checkToDate,
          purchaser_ids: filterInfo.purchaser.map(purchaser => Number(purchaser.value)),
        },
        isLoading || isRefreshing
      );

      set(accountingAtom, {
        ...get(accountingAtom),
        journalEntries: response?.journalEntries ?? [],
        jeColumns: response?.columns ?? [],
        jeInnerColumns: response?.innerColumns ?? [],
        jeTotalCount: response?.totalCount ?? 0,
        jeCurrentPage: response?.currentPage ?? 1,
        jePageLimit: response?.pageLimit ?? 0,
        jeIsLoading: false,
        jeIsRefreshing: false,
        jeError: null,
      });
    } catch (error) {
      logger.error("Failed to fetch accounting journal entries", error);
      set(accountingAtom, {
        ...get(accountingAtom),
        jeIsLoading: false,
        jeIsRefreshing: false,
        jeError: error as Error,
      });
    }
  }
);

export const setJeFilterAtom = atom(null, (get, set, filter: AccountingRemittanceFilterInfo) => {
  set(accountingAtom, {
    ...get(accountingAtom),
    jeFilterInfo: filter,
  });
});

export const clearJeFilterAtom = atom(null, (get, set) => {
  set(accountingAtom, {
    ...get(accountingAtom),
    jeIsFilterApplied: false,
    jeFilterInfo: {
      lastFilteredAt: null,
      search: null,
      checkFromDate: null,
      checkToDate: null,
      checkDateRange: null,
      purchaser: [],
      sortColumn: null,
      sortOrder: null,
    },
  });
});

export const setJeIsFilterAppliedAtom = atom(null, (get, set, jeIsFilterApplied: boolean) => {
  set(accountingAtom, {
    ...get(accountingAtom),
    jeIsFilterApplied,
  });
});

export const setJeCurrentPageAtom = atom(null, (get, set, jeCurrentPage: number) => {
  set(accountingAtom, {
    ...get(accountingAtom),
    jeCurrentPage,
  });
});

export const setExpandedJeRemittanceIdAtom = atom(null, (get, set, expandedJeRemittanceId: number | null) => {
  set(accountingAtom, {
    ...get(accountingAtom),
    expandedJeRemittanceId,
  });
});
